import { useState } from 'react';
import { Moon, ArrowRight, Check, ShieldCheck } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS = [
  {
    key: 'retirement_plan',
    text: 'I need help starting a RETIREMENT PLAN or need a professional review of my current portfolio.',
  },
  {
    key: 'college_savings',
    text: 'I need help starting a COLLEGE SAVINGS PLAN.',
  },
  {
    key: 'reduce_debt',
    text: 'I am overwhelmed and need guidance on how to REDUCE MY DEBT.',
  },
  {
    key: 'life_insurance',
    text: 'I do not have LIFE INSURANCE policies, or I am not sure if I have enough.',
  },
  {
    key: 'start_investing',
    text: 'I want to start INVESTING so my money will keep up with the rate of inflation.',
  },
];

const YesNo = ({ value, onChange, name }) => (
  <div className="flex gap-2" data-testid={`yesno-${name}`}>
    {['Yes', 'No'].map((label) => {
      const selected = value === (label === 'Yes');
      return (
        <button
          type="button"
          key={label}
          onClick={() => onChange(label === 'Yes')}
          className={`min-w-[76px] px-5 py-2.5 rounded-full font-manrope text-sm font-medium border transition-all ${
            selected
              ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]'
              : 'bg-white/5 text-zinc-300 border-white/10 hover:border-[#D4AF37]/40 hover:text-white'
          }`}
          data-testid={`${name}-${label.toLowerCase()}`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

export const WealthSurvey = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setAnswer = (key, val) => setAnswers((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email) {
      toast.error('Please enter your name and email');
      return;
    }
    const missing = QUESTIONS.find((q) => typeof answers[q.key] !== 'boolean');
    if (missing) {
      toast.error('Please answer every question — Yes or No');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await fetch(`${API}/survey/wealth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone: phone || undefined,
          retirement_plan: answers.retirement_plan,
          college_savings: answers.college_savings,
          reduce_debt: answers.reduce_debt,
          life_insurance: answers.life_insurance,
          start_investing: answers.start_investing,
        }),
      });
      if (resp.ok) {
        setSubmitted(true);
        toast.success('Thank you — your responses are safe with us.');
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Could not submit. Please try again.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="wealth-survey"
      className="py-24 px-6 bg-[#0A0A0A] border-t border-white/5"
      data-testid="wealth-survey-section"
    >
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6">
            <Moon className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-xs font-bold mb-4">
            A Financial Wellness Check-In
          </p>
          <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-white leading-tight mb-4">
            What keeps you up
            <span className="italic text-[#D4AF37]"> at night?</span>
          </h2>
          <p className="text-zinc-400 font-manrope max-w-xl mx-auto leading-relaxed">
            Five honest questions. Two minutes. You'll walk away clearer on where you stand and
            where you're headed — and we'll follow up with the right support before Panama.
          </p>
        </div>

        {submitted ? (
          <div
            className="bg-[#111111] border border-[#D4AF37]/30 rounded-2xl p-10 text-center"
            data-testid="survey-success"
          >
            <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h3 className="font-cormorant text-3xl font-medium text-white mb-3">
              Thank you, brother.
            </h3>
            <p className="text-zinc-400 font-manrope max-w-md mx-auto">
              Your responses are confidential. DeWayne Ellis and the Reset team will reach out with
              tailored resources before the retreat.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-[#111111] border border-white/10 rounded-2xl p-6 md:p-10 space-y-8"
            data-testid="survey-form"
          >
            {/* Questions */}
            <div className="space-y-6">
              {QUESTIONS.map((q, idx) => (
                <div
                  key={q.key}
                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b border-white/5 last:border-b-0"
                  data-testid={`question-${idx + 1}`}
                >
                  <p className="text-zinc-200 font-manrope text-sm md:text-base leading-relaxed flex-1">
                    <span className="text-[#D4AF37] font-semibold mr-2">Q{idx + 1}.</span>
                    {q.text}
                  </p>
                  <YesNo
                    value={answers[q.key]}
                    onChange={(v) => setAnswer(q.key, v)}
                    name={q.key}
                  />
                </div>
              ))}
            </div>

            {/* Contact info */}
            <div className="grid md:grid-cols-2 gap-4 pt-2">
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12"
                data-testid="survey-name-input"
                required
              />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12"
                data-testid="survey-email-input"
                required
              />
            </div>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Cell # (optional)"
              className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12"
              data-testid="survey-phone-input"
            />

            {/* Submit */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
              <p className="text-zinc-500 font-manrope text-xs flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
                Confidential · we never share your responses.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#D4AF37] text-[#0A0A0A] px-8 h-12 rounded-full font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50 group"
                data-testid="survey-submit-btn"
              >
                {submitting ? 'Submitting...' : (
                  <>
                    Submit responses
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </Button>
            </div>

            {/* Footer attribution */}
            <div className="text-center pt-4 border-t border-white/5">
              <p className="text-zinc-600 font-manrope text-xs italic">
                Creating a Pathway to Wealth ·{' '}
                <a
                  href="https://www.thewealthsyndicate.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] hover:underline"
                >
                  thewealthsyndicate.com
                </a>
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
